import type { ComponentPublicInstance } from 'vue'
export function getUaMatchedMap(ua: string) {
  return {
    // 内核
    kernel: {
      Trident: ua.indexOf('Trident') > -1 || ua.indexOf('NET CLR') > -1,
      Presto: ua.indexOf('Presto') > -1,
      WebKit: ua.indexOf('AppleWebKit') > -1,
      Gecko: ua.indexOf('Gecko/') > -1,
    },

    // 浏览器
    browser: {
      Safari: ua.indexOf('Safari') > -1,
      Chrome: ua.indexOf('Chrome') > -1 || ua.indexOf('CriOS') > -1,
      IE: ua.indexOf('MSIE') > -1 || ua.indexOf('Trident') > -1,
      Edge: ua.indexOf('Edge') > -1,
      Firefox: ua.indexOf('Firefox') > -1 || ua.indexOf('FxiOS') > -1,
      Chromium: ua.indexOf('Chromium') > -1,
      Opera: ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1,
      360: ua.indexOf('QihooBrowser') > -1 || ua.indexOf('QHBrowser') > -1,
      UC: ua.indexOf('UC') > -1 || ua.indexOf(' UBrowser') > -1,
      QQBrowser: ua.indexOf('QQBrowser') > -1,
      Baidu: ua.indexOf('Baidu') > -1 || ua.indexOf('BIDUBrowser') > -1,
      XiaoMi: ua.indexOf('MiuiBrowser') > -1,
      Wechat: ua.indexOf('MicroMessenger') > -1,
    },

    // 系统或平台
    os: {
      Windows: ua.indexOf('Windows') > -1,
      MacintoshOS: ua.indexOf('Macintosh') > -1,
      Android: ua.indexOf('Android') > -1 || ua.indexOf('Adr') > -1,
      Ubuntu: ua.indexOf('Ubuntu') > -1,
      iOS: ua.indexOf('iPhone') > -1,
      iPadOS: ua.indexOf('iPad') > -1,
    },
  }
}

// 获取操作系统版本
export function getOSVersion(ua: string, os: string) {
  let osVersion = 'unknown'

  switch (os) {
    case 'Windows':
      osVersion = ua.replace(/^.*Windows NT ([\d.]+);.*$/, '$1')
      break
    case 'MacintoshOS':
      osVersion = ua.replace(/^.*Mac OS X ([\d_]+).*$/, '$1').replace(/_/g, '.')
      break
    case 'Android':
      osVersion = ua.replace(/^.*Android ([\d.]+);.*$/, '$1')
      break
    case 'iOS':
      osVersion = ua.replace(/^.*OS ([\d_]+) like.*$/, '$1').replace(/_/g, '.')
      break
    case 'iPadOS':
      osVersion = ua.replace(/^.*OS ([\d_]+) like.*$/, '$1').replace(/_/g, '.')
      break
  }

  return osVersion
}

// 获取浏览器版本
export function getBrowserVersion(ua: string, browser: string) {
  let version = 'unknown'
  switch (browser) {
    case 'Safari':
      version = ua.replace(/^.*Version\/([\d.]+).*$/, '$1')
      break
    case 'Chrome':
      version = ua.replace(/^.*Chrome\/([\d.]+).*$/, '$1')
      break
    case 'IE':
      version = ua.replace(/^.*MSIE ([\d.]+).*$/, '$1').replace(/^.*rv:([\d.]+).*$/, '$1')
      break
    case 'Edge':
      version = ua.replace(/^.*Edge\/([\d.]+).*$/, '$1')
      break
    case 'Firefox':
      version = ua.replace(/^.*Firefox\/([\d.]+).*$/, '$1')
      break
    case 'Chromium':
      version = ua.replace(/^.*Chromium\/([\d.]+).*$/, '$1')
      break
    case 'Opera':
      version = ua.replace(/^.*Opera\/([\d.]+).*$/, '$1').replace(/^.*OPR\/([\d.]+).*$/, '$1')
      break
    case '360':
      version = ua.replace(/^.*QihooBrowser\/([\d.]+).*$/, '$1')
      break
    case 'UC':
      version = ua.replace(/^.*UC?Browser\/([\d.]+).*$/, '$1')
      break
    case 'QQBrowser':
      version = ua.replace(/^.*QQBrowser\/([\d.]+).*$/, '$1')
      break
    case 'Baidu':
      version = ua.replace(/^.*BIDUBrowser[\s/]([\d.]+).*$/, '$1')
      break
    case 'XiaoMi':
      version = ua.replace(/^.*MiuiBrowser\/([\d.]+).*$/, '$1')
      break
    case 'Wechat':
      version = ua.replace(/^.*MicroMessenger\/([\d.]+).*$/, '$1')
      break
  }

  return version
}

// 从UA匹配的映射中获取值
export function getValueFromUaMatchedMap(obj: Recordable<boolean>) {
  return (
    Object.entries(obj).reduce((s, [key, bool]) => {
      return bool && (s = key), s
    }, '') || 'unknown'
  )
}

// 获取地理位置
export function getLocation(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0,
    })
  })
}

// 获取客户端IP或代理IP
export function getIP() {
  const ipRE = /(([1-9]?\d|1\d{2}|2[0-4]\d|25[0-5])\.){3}([1-9]?\d|1\d{2}|2[0-4]\d|25[0-5])/
  const iceServers: RTCIceServer[] = [
    {
      urls: 'stun:stun01.sipphone.com',
    },
    {
      urls: 'stun:stun.ekiga.net',
    },
    {
      urls: 'stun:stun.fwdnet.net',
    },
    {
      urls: 'stun:stun.l.google.com:19302',
    },
  ]

  return new Promise<string>((resolve, reject) => {
    const pc = new RTCPeerConnection({
      iceServers,
    })

    let ip = ''

    pc.createDataChannel('dc')

    pc.addEventListener('icecandidate', (e) => {
      if (e.candidate && ip === '') {
        const address = e.candidate.address
        if (address) {
          ip = (ipRE.test(address) && address) || ''
        } else {
          const ipMatched = e.candidate.candidate.match(ipRE)
          ip = (ipMatched && ipMatched[0]) || ''
        }
      } else {
        resolve(ip)
      }
    })

    pc.createOffer()
      .then((offer) => {
        pc.setLocalDescription(offer)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

const classifyRE = /(?:^|[_-])(\w)/g
const classify = (name: string): string =>
  name.replace(classifyRE, (m) => m.toLocaleUpperCase()).replace(/[-_]/g, '')

const ROOT_COMPONENT_NAME = '<Root>'
const ANONYMOUS_COMPONENT_NAME = '<Anonymous>'

// 格式化Vue组件名称
export function formatComponentName(vm: ComponentPublicInstance | null): string {
  if (!vm) {
    return ANONYMOUS_COMPONENT_NAME
  }
  if (vm.$root === vm) {
    return ROOT_COMPONENT_NAME
  }

  const options = vm.$options
  const file = options.__file
  let name: string = options.name || options._componentTag

  if (!name && file) {
    const matched = file.match(/([^/\\]+)\.vue/)
    name = (matched && matched[1]) || ''
  }

  return name ? `<${classify(name)}>` : `${ANONYMOUS_COMPONENT_NAME}` + file ? `at ${file}` : ''
}

export function generateComponentTrace(vm: ComponentPublicInstance | null) {
  if (vm?.$parent) {
    const tree: any[] = []
    let currentRecursiveSequence = 0

    while (vm) {
      if (tree.length > 0) {
        const last = tree[tree.length - 1] as any
        if (last.$options.name && last.$options.name === vm.$options.name) {
          currentRecursiveSequence += 1
          vm = vm.$parent
          continue
        } else if (currentRecursiveSequence > 0) {
          tree[tree.length - 1] = [last, currentRecursiveSequence]
          currentRecursiveSequence = 0
        }
      }
      tree.push(vm)
      vm = vm.$parent
    }
    const formattedTree = tree
      .map(
        (vm, i) =>
          `${
            (i === 0 ? '---> ' : ' '.repeat(5 + i * 2)) +
            (Array.isArray(vm)
              ? `${formatComponentName(vm[0])}... (${vm[1]} recursive calls)`
              : formatComponentName(vm))
          }`,
      )
      .join('\n')

    return `\n\nfound in\n\n${formattedTree}`
  }

  return `\n\n(found in ${formatComponentName(vm)})`
}
